import { useState } from "react";
import { Label } from "@groot/ui/label";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Textarea } from "@groot/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@groot/ui/dialog";
import { toast } from "sonner";
import {
  useCustomers,
  useCreateCustomer,
  useDeleteCustomer,
} from "@/pages/customer/hooks/useCustomers";
import { PageLayout } from "@groot/shell/components/layout/PageLayout";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { SkeletonTable } from "@groot/ui/loading-skeleton";
import { EmptyState } from "@groot/ui/empty-state";
import { RowActions, RowActionItem, RowActionSeparator } from "@/components/row-actions";
import { Search, UserPlus, Users, Trash2, Eye } from "lucide-react";

const customerSchema = z.object({
  name: z.string().min(1, "Name is required"),
  mobileNumber: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  instagramHandle: z.string().optional(),
  address: z.string().optional(),
  notes: z.string().optional(),
});

type CustomerFormValues = z.infer<typeof customerSchema>;

export function Customers() {
  const { data: customers, isLoading } = useCustomers();
  const createCustomer = useCreateCustomer();
  const deleteCustomer = useDeleteCustomer();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: "",
      mobileNumber: "",
      email: "",
      instagramHandle: "",
      address: "",
      notes: "",
    },
  });

  const filteredCustomers = customers?.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      (c.mobileNumber?.toLowerCase() ?? "").includes(q) ||
      (c.email?.toLowerCase() ?? "").includes(q)
    );
  });

  const onSubmit = async (values: CustomerFormValues) => {
    try {
      await createCustomer.mutateAsync(values);
      toast.success("Success", { description: "Customer created" });
      form.reset();
      setOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to create customer" });
    }
  };

  const removeCustomer = async (id: number) => {
    try {
      await deleteCustomer.mutateAsync(id);
      toast.success("Deleted", { description: "Customer removed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to delete customer" });
    }
  };

  const actions = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="size-4" />
          Add Customer
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Customer</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-2">
            <Label>Name</Label>
            <Input placeholder="Customer name" {...form.register("name")} />
            {form.formState.errors.name && (
              <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Mobile Number</Label>
            <Input placeholder="Mobile number (optional)" {...form.register("mobileNumber")} />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input placeholder="Email (optional)" {...form.register("email")} />
          </div>
          <div className="space-y-2">
            <Label>Instagram Handle</Label>
            <Input placeholder="@username (optional)" {...form.register("instagramHandle")} />
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Textarea placeholder="Address (optional)" {...form.register("address")} />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea placeholder="Notes (optional)" {...form.register("notes")} />
          </div>
          <Button className="w-full" type="submit" disabled={createCustomer.isPending}>
            {createCustomer.isPending ? "Creating..." : "Create"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <PageLayout title="Customers" description="Manage your customers" actions={actions}>
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, mobile, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {isLoading && (
          <div className="overflow-hidden rounded-lg border">
            <SkeletonTable columns={5} rows={6} />
          </div>
        )}

        {!isLoading && filteredCustomers && filteredCustomers.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            {/* Mobile: one tappable row per customer */}
            <ul className="md:hidden divide-y divide-border">
              {filteredCustomers.map((customer) => (
                <li key={customer.id} className="flex items-center gap-2 py-3 pl-4 pr-2">
                  <Link to={`/customers/${customer.id}`} className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {customer.name}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {[customer.mobileNumber, customer.email].filter(Boolean).join(" · ") ||
                        "No contact details"}
                    </div>
                  </Link>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {customer._count?.orders ?? 0} orders
                  </span>
                  <CustomerRowActions
                    customerId={customer.id}
                    onDelete={removeCustomer}
                    deleting={deleteCustomer.isPending}
                  />
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead className="hidden sm:table-cell">Email</TableHead>
                    <TableHead className="hidden sm:table-cell">Orders</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.map((customer) => (
                    <TableRow
                      key={customer.id}
                      className="cursor-pointer hover:bg-muted/50 border-border"
                    >
                      <TableCell className="text-sm text-foreground whitespace-normal">
                        <Link
                          to={`/customers/${customer.id}`}
                          className="font-medium hover:text-primary"
                        >
                          {customer.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {customer.mobileNumber ?? "—"}
                      </TableCell>
                      <TableCell
                        className="text-sm text-muted-foreground hidden sm:table-cell max-w-56 truncate"
                        title={customer.email ?? undefined}
                      >
                        {customer.email ?? "—"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">
                        {customer._count?.orders ?? 0}
                      </TableCell>
                      <TableCell>
                        <CustomerRowActions
                          customerId={customer.id}
                          onDelete={removeCustomer}
                          deleting={deleteCustomer.isPending}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {!isLoading && filteredCustomers?.length === 0 && (
          <EmptyState
            icon={Users}
            title={search ? "No customers found" : "No customers yet"}
            description={
              search
                ? "Try adjusting your search terms"
                : "Get started by adding your first customer"
            }
            action={
              !search && (
                <Button onClick={() => setOpen(true)}>
                  <UserPlus className="size-4" />
                  Add Customer
                </Button>
              )
            }
          />
        )}
      </div>
    </PageLayout>
  );
}

function CustomerRowActions({
  customerId,
  onDelete,
  deleting,
}: {
  customerId: number;
  onDelete: (id: number) => void;
  deleting: boolean;
}) {
  return (
    <RowActions>
      <RowActionItem asChild>
        <Link to={`/customers/${customerId}`}>
          <Eye className="size-4" />
          View
        </Link>
      </RowActionItem>
      <RowActionSeparator />
      <RowActionItem destructive onClick={() => onDelete(customerId)} disabled={deleting}>
        <Trash2 className="size-4" />
        Delete
      </RowActionItem>
    </RowActions>
  );
}
